import { Button, Card, CardHeader, CardHeaderActions, CardHeading, Empty, H4, Icon, IconButton } from "@dgs/core";
import { StyledTextControl } from "@dgs/core/lib/theme/theme";
import { useFormikContext } from "formik";
import { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { CompanionResource, RegistrationFormSectionResourceTypeCompanionSection } from "../../../shared/types";
import { CurrentCompanion, useRegistrationForm } from "../../provider/registrationFormProviderContext";
import { toDataField } from "../../provider/registrationFormProviderUtils";
import { toGuestAttributeChildrenFromSection } from "../../registrationFormUtils";
import { validateCompanionSection } from "../validateCompanionSection";
import { RegistrationCompanionInfo } from "./RegistrationCompanionInfo";
import { RegistrationFormCompanionDrawer } from "./RegistrationFormCompanionDrawer";

const Header = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	flex-wrap: wrap;
	padding-bottom: ${({ theme }) => theme.spacing(6)};

	${({ theme }) => theme.breakpoints.tablet} {
		justify-content: space-between;
	}
`;

const Heading = styled.div`
	display: grid;
	grid-auto-flow: column;
	grid-auto-columns: max-content;
	align-items: center;
	gap: ${({ theme }) => theme.spacing(2)};
`;

const Count = styled.span<StyledTextControl>`
	${({ theme }) => theme.forms.label};
	font-size: ${({ theme }) => theme.fonts.sizes.normal};
`;

const IconButtonWithTitle = styled(IconButton)`
	pointer-events: auto;
`;

const ErrorText = styled.div`
	${({ theme }) => theme.forms.error};
`;

const CompanionGrid = styled.div`
	display: grid;
	gap: ${({ theme }) => theme.spacing(4)};
`;

interface Props {
	stepId: number;
	section: RegistrationFormSectionResourceTypeCompanionSection;
}

type CompanionDrawer =
	| "closed"
	| { companion: CurrentCompanion; open: true }
	| { companion: CurrentCompanion; open: false };

export const RegistrationCompanionSection: FC<Props> = ({ stepId, section }) => {
	const { t } = useTranslation();
	const { values } = useFormikContext<Record<number, any>>();
	const [companionDrawer, setCompanionDrawer] = useState<CompanionDrawer>("closed");
	const { companionsByType: allCompanions, removeCompanion, filledSteps, validateCompanions } = useRegistrationForm();
	const dataFields = useMemo(() => toGuestAttributeChildrenFromSection(section).map(toDataField), [section]);
	const companionSectionSettings = section.companionSettings;
	const companionType = companionSectionSettings.companionType;
	const { submitCount } = useFormikContext();

	const onNewClick = useCallback(() => {
		const emptyCompanion: CompanionResource = {
			isEditable: false,
			guestId: null,
			typeId: companionType.id,
			dataFieldValues: dataFields.map((x) => ({ id: x.id, value: null })),
			sectionId: section.id,
		};

		setCompanionDrawer({
			open: true,
			companion: { index: -1, companionType: companionType, companion: emptyCompanion },
		});
	}, [companionType, dataFields, section.id]);

	const companions = allCompanions.find((type) => type.id === companionType.id)?.companions ?? [];

	const currentCount = companions.length;
	const maxCount = companionSectionSettings.maxCompanionCount;
	const isAddButtonDisabled = companions.length >= maxCount;
	const addTitle = isAddButtonDisabled
		? t("Maximum amount of ({{companions}}) already added", {
				companions: companionType.pluralLabel,
			})
		: t("Add {{companion}}", { companion: companionType.singularLabel });
	const [error, setError] = useState<Record<number, string> | null>(() => {
		return validateCompanionSection(section, currentCount, t);
	});
	const companionErrors = useMemo(
		() => validateCompanions(stepId, values, section),
		[section, stepId, validateCompanions, values],
	);
	const hasCompanionErrors = Object.keys(companionErrors).length > 0;

	return (
		<>
			<Header>
				<div>
					<Heading>
						<H4>{companionType.pluralLabel}</H4>
						<Count>({t("{{ currentCount }} of {{ maxCount }}", { currentCount, maxCount })})</Count>
					</Heading>
					{error && submitCount > 0 && <ErrorText>{`${error[section.id]}`}</ErrorText>}
					{!error && hasCompanionErrors && submitCount > 0 && (
						<ErrorText>
							{t("Please complete the data of all {{companions}}", { companions: companionType.pluralLabel })}
						</ErrorText>
					)}
				</div>
				<Button
					title={addTitle}
					disabled={isAddButtonDisabled}
					type="button"
					onClick={onNewClick}
					color="primary"
					prefix={<Icon $icon="plus" title={addTitle} />}
				>
					{t("Add {{companion}}", { companion: companionType.singularLabel })}
				</Button>
			</Header>
			{!companions.length && <Empty>{t("No {{companions}}", { companions: companionType.pluralLabel })}</Empty>}
			<CompanionGrid>
				{companions.map((companion, index) => {
					const isEditButtonDisabled = companion.isEditable;
					const editTitle = isEditButtonDisabled
						? t("{{companion}} is already registered", {
								companion: companionType.singularLabel,
							})
						: t("Edit {{companion}}", { companion: companionType.singularLabel });

					return (
						<Card
							key={index}
							$color={section.backgroundColor === "background" ? "background" : "content"}
							$variant="outlined"
						>
							<CardHeader>
								<CardHeading>{`${index + 1}. ${companionType.singularLabel}`}</CardHeading>
								<CardHeaderActions>
									<IconButtonWithTitle
										size="small"
										onClick={() =>
											setCompanionDrawer({
												open: true,
												companion: {
													index,
													companionType: companionType,
													companion: companion,
												},
											})
										}
										type="button"
										disabled={isEditButtonDisabled}
										title={editTitle}
										icon="edit"
									/>
									<IconButton
										size="small"
										onClick={() => removeCompanion(index, companionType.id, setError, section)}
										type="button"
										color="danger"
										icon="delete"
										title={t("Remove {{companion}}", { companion: companionType.singularLabel })}
									/>
								</CardHeaderActions>
							</CardHeader>
							<RegistrationCompanionInfo
								filledSteps={{ ...filledSteps, [stepId]: values }}
								companion={companion}
								dataFields={dataFields}
								dataFieldErrors={submitCount > 0 ? companionErrors[index] : undefined}
							/>
						</Card>
					);
				})}
			</CompanionGrid>
			{companionDrawer !== "closed" && (
				<RegistrationFormCompanionDrawer
					open={companionDrawer.open}
					stepId={stepId}
					section={section}
					currentCompanion={companionDrawer.companion}
					onClose={() => setCompanionDrawer({ ...companionDrawer, open: false })}
					onClosed={() => setCompanionDrawer("closed")}
					currentStepValues={values}
					companionTypeId={companionType.id}
					setError={setError}
				/>
			)}
		</>
	);
};
